import type { Active, DragEndEvent, Over } from '@dnd-kit/core';
import {
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import type { LevelGroup } from '@/components/common/settings/item-types/lib/types';
import type { ItemType, ItemTypeLevel } from '@/lib/api/types/itemType';

export const transformItemsToLevelGroups = (
  itemTypes: ItemType[],
  itemLevels: ItemTypeLevel[],
): LevelGroup[] => {
  const groupedByLevel = itemTypes.reduce<Record<number, ItemType[]>>((groupedItemTypes, item) => {
    if (!(item.itemLevelId in groupedItemTypes)) {
      groupedItemTypes[item.itemLevelId] = [];
    }
    groupedItemTypes[item.itemLevelId]?.push(item);
    return groupedItemTypes;
  }, {});

  return itemLevels
    .map((levelData) => ({
      levelId: levelData.id,
      level: levelData.level,
      levelName: levelData.name,
      levelData: levelData,
      items: groupedByLevel[levelData.id] ?? [],
    }))
    .sort((a, b) => a.level - b.level);
};

export const createDragSensors = () =>
  useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {}),
  );

export const createCollisionDetection = (activeDragType: 'row' | 'item' | null) => {
  return (args: Parameters<typeof closestCenter>[0]) => {
    if (activeDragType === 'item') {
      const pointerCollisions = pointerWithin(args);
      const filteredCollisions = pointerCollisions.filter(
        (collision) => collision.data?.droppableContainer?.data?.current?.type === 'levelGroup',
      );
      return filteredCollisions.length > 0 ? filteredCollisions : rectIntersection(args);
    }
    const centerCollisions = closestCenter(args);
    return centerCollisions.filter(
      (collision) =>
        !collision.data?.droppableContainer?.data?.current?.type ||
        collision.data.droppableContainer?.data?.current?.type !== 'levelGroup',
    );
  };
};

export const getDragModifiers = (activeDragType: 'row' | 'item' | null) =>
  activeDragType === 'row'
    ? [restrictToParentElement, restrictToVerticalAxis]
    : [restrictToWindowEdges];

export const handleRowReorder = (levelGroups: LevelGroup[], activeId: string, overId: string) => {
  const activeIndex = levelGroups.findIndex((group) => group.levelId.toString() === activeId);
  const overIndex = levelGroups.findIndex((group) => group.levelId.toString() === overId);

  if (activeIndex !== -1 && overIndex !== -1) {
    return arrayMove(levelGroups, activeIndex, overIndex);
  }
  return levelGroups;
};

export const shouldHandleDrop = (active: DragEndEvent['active'], over: DragEndEvent['over']) =>
  !!over && active.id !== over.id;

export const isItemToLevelDrop = (active: Active, over: Over | null) => {
  const activeData = active.data.current;
  const overData = over?.data.current;

  return activeData?.type === 'itemType' && overData?.type === 'levelGroup';
};

export const isRowReorder = (active: Active, over: Over | null) => {
  const activeData = active.data.current;
  const overData = over?.data.current;

  return !activeData?.type && !overData?.type;
};

export const shouldUpdateItemTypeLevel = (itemType: ItemType, targetLevelId: number) => {
  return itemType.itemLevelId !== targetLevelId;
};

export const handleItemToLevelDrop = ({
  levelGroups,
  itemType,
  targetLevelId,
}: {
  levelGroups: LevelGroup[];
  itemType: ItemType;
  targetLevelId: number;
}): LevelGroup[] =>
  levelGroups.map((group) => {
    if (group.levelId === itemType.itemLevelId) {
      return {
        ...group,
        items: group.items.filter((item) => item.id !== itemType.id),
      };
    }

    if (group.levelId === targetLevelId) {
      return {
        ...group,
        items: [...group.items, { ...itemType, itemLevelId: targetLevelId }],
      };
    }

    return group;
  });

export const shouldDisableAddHierarchyButton = ({
  itemTypes,
  itemTypeLevels,
}: {
  itemTypes: ItemType[];
  itemTypeLevels: ItemTypeLevel[];
}) =>
  itemTypeLevels.some((level) => !itemTypes.some((itemType) => itemType.itemLevelId === level.id));
